import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  IconButton,
  Grid,
  Paper,
  Badge,
  Fade,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  EmojiEvents,
  WorkspacePremium,
  Star,
  TrendingUp,
  Person,
  PhotoCamera,
  Edit,
  Delete,
  MoreVert
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import UsernameModal from './UsernameModal';

const Leaderboard = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userPosition, setUserPosition] = useState(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [updating, setUpdating] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    loadLeaderboardData();
  }, []);

  const loadLeaderboardData = async () => {
    try {
      setLoading(true);
      
      // Check if user has username first
      const userPositionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/my-position`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (userPositionResponse.ok) {
        const positionData = await userPositionResponse.json();
        setUserPosition(positionData);
        
        // Show username modal if user doesn't have a username, but continue loading leaderboard
        if (!positionData.hasUsername && !showUsernameModal) {
          setShowUsernameModal(true);
        }
      }

      // Always load leaderboard data, even if user doesn't have username
      const leaderboardResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (leaderboardResponse.ok) {
        const data = await leaderboardResponse.json();
        console.log('Leaderboard data received:', data.leaderboard);
        console.log('First user avatar URL:', data.leaderboard[0]?.avatar_url);
        setLeaderboardData(data.leaderboard);
      } else {
        throw new Error('Failed to load leaderboard');
      }

    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setError('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameSet = () => {
    setShowUsernameModal(false);
    loadLeaderboardData();
  };

  const handleEditUsername = () => {
    setNewUsername(userPosition.username);
    setEditDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDeleteListing = () => {
    setDeleteDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleUpdateAvatar = () => {
    setAvatarDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be smaller than 5MB');
        return;
      }

      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleAvatarUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }

    try {
      setUpdating(true);
      
      const formData = new FormData();
      formData.append('avatar', selectedFile);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAvatarDialogOpen(false);
        setSelectedFile(null);
        setPreviewUrl(null);
        setError('');
        loadLeaderboardData(); // Reload leaderboard data to show new avatar
      } else {
        // Handle different response types
        let errorMessage = 'Failed to update avatar';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch (e) {
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setError('Failed to upload avatar');
    } finally {
      setUpdating(false);
    }
  };

  const handleAvatarDialogClose = () => {
    setAvatarDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
  };

  const handleUpdateUsername = async () => {
    if (!newUsername || newUsername.trim().length < 3 || newUsername.trim().length > 20) {
      setError('Username must be between 3 and 20 characters');
      return;
    }

    try {
      setUpdating(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/username`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ username: newUsername.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setEditDialogOpen(false);
        setError('');
        loadLeaderboardData(); // Reload leaderboard data
      } else {
        // Handle different response types
        let errorMessage = 'Failed to update username';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch (e) {
          // If response is not JSON (like HTML 404 page), use status text
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error updating username:', error);
      setError('Failed to update username');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFromLeaderboard = async () => {
    try {
      setUpdating(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/my-entry`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDeleteDialogOpen(false);
        setError('');
        // Redirect to conversation or refresh the page since user is no longer on leaderboard
        navigate('/conversation');
      } else {
        // Handle different response types
        let errorMessage = 'Failed to remove from leaderboard';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch (e) {
          // If response is not JSON (like HTML 404 page), use status text
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error removing from leaderboard:', error);
      setError('Failed to remove from leaderboard');
    } finally {
      setUpdating(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <EmojiEvents sx={{ color: '#FFD700', fontSize: 32 }} />;
      case 2:
        return <WorkspacePremium sx={{ color: '#C0C0C0', fontSize: 28 }} />;
      case 3:
        return <WorkspacePremium sx={{ color: '#CD7F32', fontSize: 28 }} />;
      default:
        return (
          <Box sx={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            bgcolor: 'primary.main', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.9rem'
          }}>
            {rank}
          </Box>
        );
    }
  };

  const getAvatarPlaceholder = (username) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ];
    const color = colors[username.charCodeAt(0) % colors.length];
    
    return (
      <Avatar
        sx={{
          bgcolor: color,
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.2rem'
        }}
      >
        {username.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  const getLevelProgress = (xp) => {
    // Use EXACT same formula as ProgressDashboard
    const level = Math.floor(Math.sqrt(xp / 100)) + 1;
    const currentLevelXP = Math.pow(level - 1, 2) * 100;
    const nextLevelXP = Math.pow(level, 2) * 100;
    const progressToNextLevel = Math.round(((xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100);
    return { level, progress: Math.max(0, Math.min(100, progressToNextLevel)) };
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading Leaderboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: '#fafbfc', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      pb: 4
    }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: 'rgba(255,255,255,0.95)', 
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        py: { xs: 2, sm: 3 }
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between"
               flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}
                 flexDirection={{ xs: 'column', sm: 'row' }} textAlign={{ xs: 'center', sm: 'left' }}>
              <IconButton 
                onClick={() => navigate('/conversation')}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
              >
                <ArrowBack sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
              
              <Box>
                <Typography variant="h4" fontWeight="600" color="#1a1a1a" sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <EmojiEvents sx={{ color: '#FFD700', fontSize: { xs: 28, sm: 32 } }} />
                  Leaderboard
                </Typography>
                <Typography variant="body1" color="#6b7280" sx={{ mt: 0.5 }}
                           fontSize={{ xs: '0.875rem', sm: '1rem' }}>
                  See how you rank among our therapy champions! 🏆
                </Typography>
              </Box>
            </Box>

            {userPosition?.hasUsername && (
              <Box display="flex" alignItems="center" gap={1}>
                <Badge badgeContent={userPosition.rank} color="primary">
                  <Paper sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: { xs: 1, sm: 1 }
                  }}>
                    <TrendingUp sx={{ color: '#667eea', fontSize: { xs: 18, sm: 24 } }} />
                    <Box>
                      <Typography variant="body2" fontWeight="600"
                                 fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                        Your Rank
                      </Typography>
                      <Typography variant="caption" color="text.secondary"
                                 fontSize={{ xs: '0.6875rem', sm: '0.75rem' }}>
                        #{userPosition.rank}
                      </Typography>
                    </Box>
                  </Paper>
                </Badge>
                <IconButton 
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  sx={{ 
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 }
                  }}
                >
                  <MoreVert sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={() => setMenuAnchorEl(null)}
                  PaperProps={{
                    sx: { mt: 1 }
                  }}
                >
                  <MenuItem onClick={handleEditUsername}>
                    <Edit sx={{ mr: 1, fontSize: 20 }} />
                    Edit Username
                  </MenuItem>
                  <MenuItem onClick={handleUpdateAvatar}>
                    <PhotoCamera sx={{ mr: 1, fontSize: 20 }} />
                    Update Avatar
                  </MenuItem>
                  <MenuItem onClick={handleDeleteListing} sx={{ color: 'error.main' }}>
                    <Delete sx={{ mr: 1, fontSize: 20 }} />
                    Remove from Leaderboard
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Top 3 Podium */}
        {leaderboardData.length >= 3 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight="700" textAlign="center" sx={{ mb: 4, color: 'white' }}>
              🏆 Wellness Champions 🏆
            </Typography>
            
            <Grid container spacing={{ xs: 1, sm: 3 }} justifyContent="center">
              {/* 2nd Place */}
              <Grid item xs={4} sm={4} md={3}>
                <Fade in timeout={800}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: { xs: 1, sm: 3 },
                    background: 'linear-gradient(135deg, #C0C0C0 0%, #E8E8E8 100%)',
                    transform: { xs: 'scale(1)', sm: 'scale(0.9)' },
                    mt: { xs: 0, sm: 2 }
                  }}>
                    <Box sx={{ position: 'relative', mb: { xs: 1, sm: 2 } }}>
                      {leaderboardData[1]?.avatar_url ? (
                        <Avatar src={leaderboardData[1].avatar_url} sx={{ 
                          width: { xs: 40, sm: 80 }, 
                          height: { xs: 40, sm: 80 }, 
                          mx: 'auto' 
                        }} />
                      ) : (
                        <Avatar sx={{ 
                          width: { xs: 40, sm: 80 }, 
                          height: { xs: 40, sm: 80 }, 
                          mx: 'auto',
                          bgcolor: '#4ECDC4',
                          fontSize: { xs: '1rem', sm: '2rem' },
                          fontWeight: 'bold'
                        }}>
                          {leaderboardData[1]?.username?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                      )}
                      <WorkspacePremium sx={{ 
                        position: 'absolute', 
                        top: { xs: -6, sm: -10 }, 
                        right: { xs: '15%', sm: '25%' }, 
                        color: '#C0C0C0', 
                        fontSize: { xs: 16, sm: 32 }
                      }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold" fontSize={{ xs: '0.75rem', sm: '1.25rem' }}>
                      {leaderboardData[1]?.username}
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="primary" fontSize={{ xs: '1.2rem', sm: '2.125rem' }}>
                      #2
                    </Typography>
                    <Chip 
                      label={`Level ${leaderboardData[1]?.level || 1}`} 
                      size="small" 
                      sx={{ mt: 1, fontSize: { xs: '0.6rem', sm: '0.8125rem' }, pointerEvents: 'none' }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} fontSize={{ xs: '0.65rem', sm: '0.875rem' }}>
                      {leaderboardData[1]?.xp || 0} XP
                    </Typography>
                  </Card>
                </Fade>
              </Grid>

              {/* 1st Place */}
              <Grid item xs={4} sm={4} md={3}>
                <Fade in timeout={600}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: { xs: 1, sm: 3 },
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
                    transform: { xs: 'scale(1)', sm: 'scale(1.1)' },
                    boxShadow: '0 8px 32px rgba(255, 215, 0, 0.3)'
                  }}>
                    <Box sx={{ position: 'relative', mb: { xs: 1, sm: 2 } }}>
                      {leaderboardData[0]?.avatar_url ? (
                        <Avatar src={leaderboardData[0].avatar_url} sx={{ 
                          width: { xs: 50, sm: 100 }, 
                          height: { xs: 50, sm: 100 }, 
                          mx: 'auto' 
                        }} />
                      ) : (
                        <Avatar sx={{ 
                          width: { xs: 50, sm: 100 }, 
                          height: { xs: 50, sm: 100 }, 
                          mx: 'auto',
                          bgcolor: '#FF6B6B',
                          fontSize: { xs: '1.2rem', sm: '2rem' },
                          fontWeight: 'bold'
                        }}>
                          {leaderboardData[0]?.username?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                      )}
                      <EmojiEvents sx={{ 
                        position: 'absolute', 
                        top: { xs: -8, sm: -15 }, 
                        right: { xs: '10%', sm: '20%' }, 
                        color: '#FF6B00', 
                        fontSize: { xs: 20, sm: 40 }
                      }} />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" color="white" fontSize={{ xs: '0.8rem', sm: '1.5rem' }}>
                      {leaderboardData[0]?.username}
                    </Typography>
                    <Typography variant="h3" fontWeight="700" color="white" fontSize={{ xs: '1.2rem', sm: '3rem' }}>
                      👑
                    </Typography>
                    <Chip 
                      label={`Level ${leaderboardData[0]?.level || 1}`} 
                      sx={{ 
                        mt: 1, 
                        bgcolor: 'rgba(255,255,255,0.9)',
                        color: '#FF6B00',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.6rem', sm: '0.8125rem' },
                        pointerEvents: 'none'
                      }}
                    />
                    <Typography variant="body1" color="white" fontWeight="600" sx={{ mt: 1 }} fontSize={{ xs: '0.7rem', sm: '1rem' }}>
                      {leaderboardData[0]?.xp || 0} XP
                    </Typography>
                  </Card>
                </Fade>
              </Grid>

              {/* 3rd Place */}
              <Grid item xs={4} sm={4} md={3}>
                <Fade in timeout={1000}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: { xs: 1, sm: 3 },
                    background: 'linear-gradient(135deg, #CD7F32 0%, #D4A574 100%)',
                    transform: { xs: 'scale(1)', sm: 'scale(0.9)' },
                    mt: { xs: 0, sm: 2 }
                  }}>
                    <Box sx={{ position: 'relative', mb: { xs: 1, sm: 2 } }}>
                      {leaderboardData[2]?.avatar_url ? (
                        <Avatar src={leaderboardData[2].avatar_url} sx={{ 
                          width: { xs: 40, sm: 80 }, 
                          height: { xs: 40, sm: 80 }, 
                          mx: 'auto' 
                        }} />
                      ) : (
                        <Avatar sx={{ 
                          width: { xs: 40, sm: 80 }, 
                          height: { xs: 40, sm: 80 }, 
                          mx: 'auto',
                          bgcolor: '#96CEB4',
                          fontSize: { xs: '1rem', sm: '2rem' },
                          fontWeight: 'bold'
                        }}>
                          {leaderboardData[2]?.username?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                      )}
                      <WorkspacePremium sx={{ 
                        position: 'absolute', 
                        top: { xs: -6, sm: -10 }, 
                        right: { xs: '15%', sm: '25%' }, 
                        color: '#CD7F32', 
                        fontSize: { xs: 16, sm: 32 }
                      }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold" fontSize={{ xs: '0.75rem', sm: '1.25rem' }}>
                      {leaderboardData[2]?.username}
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="primary" fontSize={{ xs: '1.2rem', sm: '2.125rem' }}>
                      #3
                    </Typography>
                    <Chip 
                      label={`Level ${leaderboardData[2]?.level || 1}`} 
                      size="small" 
                      sx={{ mt: 1, fontSize: { xs: '0.6rem', sm: '0.8125rem' }, pointerEvents: 'none' }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} fontSize={{ xs: '0.65rem', sm: '0.875rem' }}>
                      {leaderboardData[2]?.xp || 0} XP
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Full Leaderboard */}
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
              <Typography variant="h5" fontWeight="700" color="#1a1a1a">
                🏅 All Champions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete rankings of all registered users
              </Typography>
            </Box>

            {leaderboardData.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Person sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No users on the leaderboard yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Be the first to set your username and claim your spot!
                </Typography>
              </Box>
            ) : (
              <Box>
                {leaderboardData.map((user, index) => {
                  const { level, progress } = getLevelProgress(user.xp);
                  const isCurrentUser = userPosition?.username === user.username;
                  
                  return (
                    <Box
                      key={user.id}
                      sx={{
                        p: 3,
                        borderBottom: index < leaderboardData.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                        bgcolor: isCurrentUser ? 'rgba(102, 126, 234, 0.05)' : 'transparent',
                        border: isCurrentUser ? '2px solid rgba(102, 126, 234, 0.2)' : 'none',
                        borderRadius: isCurrentUser ? 2 : 0,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={1.5} sm={1}>
                          {getRankIcon(user.rank)}
                        </Grid>
                        
                        <Grid item xs={2.5} sm={2}>
                          {user.avatar_url ? (
                            <Avatar src={user.avatar_url} sx={{ 
                              width: { xs: 30, sm: 50 }, 
                              height: { xs: 30, sm: 50 } 
                            }} />
                          ) : (
                            <Avatar sx={{ 
                              width: { xs: 30, sm: 50 }, 
                              height: { xs: 30, sm: 50 },
                              bgcolor: '#4ECDC4',
                              fontSize: { xs: '0.8rem', sm: '1.2rem' },
                              fontWeight: 'bold'
                            }}>
                              {user.username?.charAt(0).toUpperCase() || '?'}
                            </Avatar>
                          )}
                        </Grid>
                        
                        <Grid item xs={8} sm={3}>
                          <Typography variant="h6" fontWeight="600" fontSize={{ xs: '0.75rem', sm: '1.25rem' }}>
                            {user.username}
                            {isCurrentUser && (
                              <Chip 
                                label="You" 
                                size="small" 
                                sx={{ 
                                  ml: 1, 
                                  bgcolor: 'primary.main', 
                                  color: 'white',
                                  fontSize: { xs: '0.55rem', sm: '0.75rem' },
                                  pointerEvents: 'none'
                                }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.6rem', sm: '0.875rem' }}>
                            Joined {new Date(user.created_at).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6} sm={2} display={{ xs: 'block', sm: 'block' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Star sx={{ color: '#FFD700', fontSize: { xs: 14, sm: 20 } }} />
                            <Typography variant="h6" fontWeight="600" fontSize={{ xs: '0.7rem', sm: '1.25rem' }}>
                              Level {level}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6} sm={2} display={{ xs: 'block', sm: 'block' }}>
                          <Typography variant="h6" fontWeight="600" color="primary" fontSize={{ xs: '0.7rem', sm: '1.25rem' }}>
                            {user.xp} XP
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.55rem', sm: '0.75rem' }}>
                              Progress to Level {level + 1}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: { xs: 4, sm: 8 },
                                borderRadius: 4,
                                bgcolor: 'rgba(0,0,0,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  background: 'linear-gradient(90deg, #667eea, #764ba2)'
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.55rem', sm: '0.75rem' }}>
                              {progress}%
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Username Modal */}
      <UsernameModal
        open={showUsernameModal}
        onClose={() => setShowUsernameModal(false)}
        onUsernameSet={handleUsernameSet}
      />

      {/* Edit Username Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Username</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            variant="outlined"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            helperText="Username must be between 3 and 20 characters"
            error={newUsername.length > 0 && (newUsername.length < 3 || newUsername.length > 20)}
            sx={{ mt: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateUsername} 
            variant="contained"
            disabled={updating || newUsername.length < 3 || newUsername.length > 20}
          >
            {updating ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove from Leaderboard?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove yourself from the leaderboard? This will:
          </Typography>
          <Box component="ul" sx={{ mt: 2, pl: 3 }}>
            <li>Remove your username and avatar from the public leaderboard</li>
            <li>Keep your progress and XP intact</li>
            <li>Allow you to rejoin the leaderboard later by setting a new username</li>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteFromLeaderboard} 
            variant="contained"
            color="error"
            disabled={updating}
          >
            {updating ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar Upload Dialog */}
      <Dialog open={avatarDialogOpen} onClose={handleAvatarDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Update Avatar</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {previewUrl ? (
              <Avatar
                src={previewUrl}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 2,
                  border: '3px solid',
                  borderColor: 'primary.main'
                }}
              />
            ) : userPosition?.avatar_url ? (
              <Avatar
                src={userPosition.avatar_url}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 2,
                  border: '3px solid',
                  borderColor: 'grey.300'
                }}
              />
            ) : (
              <Avatar
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '3rem',
                  fontWeight: 'bold',
                  border: '3px solid',
                  borderColor: 'grey.300'
                }}
              >
                {userPosition?.username?.charAt(0).toUpperCase() || '?'}
              </Avatar>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose an image file (max 5MB)
            </Typography>
            
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="avatar-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="avatar-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCamera />}
                sx={{ mb: 2 }}
              >
                Choose Image
              </Button>
            </label>
          </Box>

          {selectedFile && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAvatarDialogClose} disabled={updating}>
            Cancel
          </Button>
          <Button 
            onClick={handleAvatarUpload} 
            variant="contained"
            disabled={updating || !selectedFile}
            startIcon={updating ? <CircularProgress size={20} /> : <PhotoCamera />}
          >
            {updating ? 'Uploading...' : 'Upload Avatar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leaderboard;
